import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Eye, EyeOff } from 'lucide-react';
import { useAuth } from '../hooks/useAuth';
import { useAuthStore } from '../store/authStore';
import { useCartStore } from '../store/cartStore';

const LoginPage: React.FC = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const inputClass =
    'w-full bg-white border-2 border-secondary/40 focus:border-primary rounded-sm px-4 py-3 text-sm text-primary placeholder:text-secondary/70 placeholder:tracking-widest placeholder:text-xs outline-none transition-colors';
  const { login } = useAuth();
  const { fetchCart } = useCartStore();
  const navigate = useNavigate();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      setIsLoading(true);
      await login(email, password);
      await fetchCart();

      // Admin, super_admin, and staff accounts land in the admin panel;
      // everyone else goes to their customer account page.
      const role = useAuthStore.getState().user?.role;
      if (role === 'admin' || role === 'super_admin' || role === 'staff') {
        navigate('/admin');
      } else {
        navigate('/account');
      }
    } catch {
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex">
      {/* Left - Form */}
      <div className="flex-1 flex items-center justify-center px-6 py-16">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="w-full max-w-sm"
        >
          <div className="text-center mb-12">
            <Link to="/">
              <img src="/logo.png" alt="Lanre Savage" className="h-16 mx-auto" />
            </Link>
            <p className="text-xs tracking-widest uppercase text-secondary font-medium mt-4">
              Welcome Back
            </p>
            <h1 className="font-serif text-3xl text-primary mt-2">Sign In</h1>
          </div>

          <form onSubmit={handleSubmit} className="space-y-8">
            <div>
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="EMAIL ADDRESS"
                required
                className={inputClass}
              />
            </div>

            <div className="relative">
              <input
                type={showPassword ? 'text' : 'password'}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="PASSWORD"
                required
                className={`${inputClass} pr-10`}
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-secondary hover:text-primary transition-colors"
              >
                {showPassword ? <EyeOff size={16} /> : <Eye size={16} />}
              </button>
            </div>

            <div className="flex justify-end">
              <Link
                to="/forgot-password"
                className="text-xs tracking-widest uppercase text-secondary hover:text-primary transition-colors"
              >
                Forgot Password?
              </Link>
            </div>

            <button
              type="submit"
              disabled={isLoading}
              className="btn-primary w-full disabled:opacity-50"
            >
              {isLoading ? 'Signing In...' : 'Sign In'}
            </button>
          </form>

          <div className="text-center mt-8">
            <p className="text-xs tracking-widest uppercase text-secondary">
              New to Lanre Savage?{' '}
              <Link
                to="/register"
                className="text-primary hover:text-secondary underline transition-colors"
              >
                Create Account
              </Link>
            </p>
          </div>
        </motion.div>
      </div>

      {/* Right - Brand Panel */}
      {/* Right - Brand Panel */}
<div className="hidden lg:block flex-1 relative overflow-hidden">
  <img
    src="https://images.unsplash.com/photo-1487222477894-8943e31ef7b2?w=1200&q=80"
    alt="Lanre Savage"
    className="w-full h-full object-cover"
  />
  <div className="absolute inset-0 bg-black/40" />
  <motion.div
    initial={{ opacity: 0 }}
    animate={{ opacity: 1 }}
    transition={{ duration: 1, delay: 0.3 }}
    className="absolute inset-0 flex items-center justify-center text-center px-12"
  >
    <div>
      <h2 className="font-serif text-5xl text-white mb-6 leading-tight">
        Timeless<br />Elegance
      </h2>
      <p className="text-xs tracking-widest uppercase text-white/70 max-w-xs mx-auto leading-relaxed">
        Sign in to access your account, track orders, and explore exclusive collections.
      </p>
    </div>
  </motion.div>
</div>
    </div>
  );
};

export default LoginPage;